import DOMPurify from "dompurify";
import { getVariableValue } from "./resolve-tokens";

export const VARIABLE_BLOCK_TOKEN_PATTERN =
  "\\{\\{\\{\\s*([^{}\\s]+)\\s*\\}\\}\\}";

const TRIPLE_BRACE_RE = new RegExp(VARIABLE_BLOCK_TOKEN_PATTERN, "g");

export const VARIABLE_BLOCK_CLASS = "sd-variable-block";
export const VARIABLE_BLOCK_ATTR = "data-variable-name";

const sanitizeHtml = (html: string): string =>
  DOMPurify.sanitize(html, { ADD_ATTR: ["target"] });

export const createBlockElement = (
  doc: Document,
  name: string,
  resolvedHtml: string,
): HTMLDivElement => {
  const div = doc.createElement("div");
  div.className = VARIABLE_BLOCK_CLASS;
  div.setAttribute(VARIABLE_BLOCK_ATTR, name);
  div.setAttribute("contenteditable", "false");
  div.innerHTML = sanitizeHtml(resolvedHtml);
  return div;
};

const isInsideElement = (node: Node, selector: string): boolean => {
  let current: Node | null = node.parentNode;
  while (current && current.nodeType === Node.ELEMENT_NODE) {
    if ((current as Element).matches(selector)) return true;
    current = current.parentNode;
  }
  return false;
};

const replaceTripleTokensInTextNode = (
  textNode: Text,
  data: Record<string, unknown> | undefined | null,
): boolean => {
  const text = textNode.textContent ?? "";
  if (text.indexOf("{{{") === -1) return false;

  const doc = textNode.ownerDocument;
  if (!doc) return false;

  const fragment = doc.createDocumentFragment();
  let lastIndex = 0;
  let didReplace = false;
  TRIPLE_BRACE_RE.lastIndex = 0;
  let match: RegExpExecArray | null = TRIPLE_BRACE_RE.exec(text);
  while (match !== null) {
    didReplace = true;
    if (match.index > lastIndex) {
      fragment.appendChild(
        doc.createTextNode(text.slice(lastIndex, match.index)),
      );
    }
    const name = match[1];
    const raw = getVariableValue(data, name);
    const resolvedHtml = raw == null ? "" : String(raw);
    fragment.appendChild(createBlockElement(doc, name, resolvedHtml));
    lastIndex = match.index + match[0].length;
    match = TRIPLE_BRACE_RE.exec(text);
  }

  if (!didReplace) return false;

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode?.replaceChild(fragment, textNode);
  return true;
};

export const templateToBlocksInDom = (
  root: Element,
  data: Record<string, unknown> | undefined | null,
) => {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    const text = node.textContent ?? "";
    if (
      text.indexOf("{{{") !== -1 &&
      !isInsideElement(node, `.${VARIABLE_BLOCK_CLASS}`)
    ) {
      targets.push(node as Text);
    }
    node = walker.nextNode();
  }
  for (const textNode of targets) {
    replaceTripleTokensInTextNode(textNode, data);
  }
};

export const templateToBlocksHtml = (
  html: string,
  data: Record<string, unknown> | undefined | null,
): string => {
  if (!html || html.indexOf("{{{") === -1) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  templateToBlocksInDom(doc.body, data);
  return doc.body.innerHTML;
};

export const blocksToTemplateInDom = (root: Element) => {
  const blocks = root.querySelectorAll(`.${VARIABLE_BLOCK_CLASS}`);
  for (const block of Array.from(blocks)) {
    const name = block.getAttribute(VARIABLE_BLOCK_ATTR);
    if (!name) continue;
    const doc = block.ownerDocument;
    if (!doc) continue;
    const text = doc.createTextNode(`{{{${name}}}}`);
    block.parentNode?.replaceChild(text, block);
  }
};

export const blocksToTemplateHtml = (html: string): string => {
  if (!html || html.indexOf(VARIABLE_BLOCK_CLASS) === -1) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  blocksToTemplateInDom(doc.body);
  return doc.body.innerHTML;
};

export const refreshBlockValues = (
  root: Element,
  data: Record<string, unknown> | undefined | null,
) => {
  const blocks = root.querySelectorAll(`.${VARIABLE_BLOCK_CLASS}`);
  for (const block of Array.from(blocks)) {
    const name = block.getAttribute(VARIABLE_BLOCK_ATTR);
    if (!name) continue;
    const raw = getVariableValue(data, name);
    const resolvedHtml = raw == null ? "" : String(raw);
    block.innerHTML = sanitizeHtml(resolvedHtml);
  }
};
